import { useCallback, useSyncExternalStore } from 'react';

// Page-local registry row selection, owned by Leads.
// Shared between the table (checkbox column) and the future mass-action toolbar
// in the header — sibling subtrees under the page shell — via a module-level
// external store, the same pattern as `useLeadsRegistrySettings`/filters.
let selectedLeadIdsState: number[] = [];

const listeners = new Set<() => void>();

const getSnapshot = () => selectedLeadIdsState;

const subscribe = (listener: () => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

const setSelectedLeadIdsState = (nextState: number[]) => {
  selectedLeadIdsState = nextState;
  listeners.forEach((listener) => {
    listener();
  });
};

export const useLeadsRegistrySelection = () => {
  const selectedLeadIds = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getSnapshot
  );

  const setSelectedLeadIds = useCallback((leadIds: number[]) => {
    setSelectedLeadIdsState(leadIds);
  }, []);

  const clearSelection = useCallback(() => {
    if (selectedLeadIdsState.length === 0) return;

    setSelectedLeadIdsState([]);
  }, []);

  // Drops selected ids that are no longer present in the current result set
  // (e.g. after a new search or filter change), matching the legacy registry.
  const pruneSelection = useCallback((validLeadIds: number[]) => {
    if (selectedLeadIdsState.length === 0) return;

    const validIdsSet = new Set(validLeadIds);
    const nextSelectedLeadIds = selectedLeadIdsState.filter((leadId) =>
      validIdsSet.has(leadId)
    );

    if (nextSelectedLeadIds.length === selectedLeadIdsState.length) return;

    setSelectedLeadIdsState(nextSelectedLeadIds);
  }, []);

  return {
    selectedLeadIds,
    setSelectedLeadIds,
    clearSelection,
    pruneSelection,
  };
};
